function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";
import SetupHint from "../onboarding/setup-hint";
import PlunkDropDown from "../../components/plunk-dropdown";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import endpoints from "../../endpoints";
import account from "../../data/account";
import { AuthBase } from "../../../plunk/service/auth-base";
import { hasTenantFeature } from "../../functions/tenant-features";
import { num, usd, cleanTitle } from "../sales/territory-common";

// Some tenant feeds store category paths HTML-encoded (Robb & Stucky:
// "Furniture &gt; Chairs &amp; Ottomans"). Decode for DISPLAY only — the raw
// value stays the filter key so it still matches the stored product_type. Also
// show the leaf so the chip isn't a full breadcrumb.
function catDecode(raw) {
  return String(raw == null ? "" : raw).replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&");
}
function catLabel(raw) {
  const decoded = catDecode(raw);
  const leaf = decoded.split(">").pop().trim();
  return leaf || decoded;
}
import { getGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import BestFitPanel from "./best-fit";
import InterestSpark from "./interest-spark";

/*
 * Product Performance Section — Phase 4 refresh (PlunkDocs
 * specs/PRODUCT_PERFORMANCE_UI.md action list + mockup
 * specs/mockups/product-performance-refresh.html).
 *
 * - SERVER paging/sort/filter over /v1/product/performance/stats/{range}.
 *   Filter contract (hardened build, 2026-08-07): free-text search is the
 *   `q=` param (server maps it to a safe escaped LIKE over title +
 *   product_id; raw where=/$where now 400s). `$orderBy` is whitelisted to
 *   the snapshot table's SNAKE_CASE column names (+ asc/desc) — camelCase
 *   model names 400. `count` is filter-aware.
 * - All 13 snapshot ranges (last_7_days populates from the first nightly
 *   run on 2026-08-03; empty until then).
 * - Δ chips vs the paired precomputed range (FE-only join by productId),
 *   pace-clamped for partial current periods; hidden for unpaired ranges
 *   and all_time.
 * - Interest sparkline per row (interest-trend endpoint, see
 *   interest-spark.jsx).
 * - "Forecast a look-alike" row action: fetches the product image
 *   client-side (image hosts serve CORS *) and POSTs it through the
 *   existing multipart similarity-forecast contract (file field + X-NAME /
 *   X-DESCRIPTION / X-APP-ID headers).
 */

// All 13 stats snapshot ranges (probed 2026-08-03: every token below except
// last_7_days serves rows; last_7_days computes nightly starting tonight).
const STAT_RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"], ["last_week", "Last Week"], ["last_7_days", "Last 7 Days"], ["this_month", "This Month"], ["last_month", "Last Month"], ["this_quarter", "This Quarter"], ["last_quarter", "Last Quarter"], ["this_year", "This Year"], ["last_year", "Last Year"], ["all_time", "All Time"]];
// PP reads product_performance_stats snapshots — preset windows only, no
// compute-on-read for custom ranges. When the global control carries a custom
// (or otherwise unsupported) token, degrade to this_month rather than 400.
const STAT_RANGE_KEYS = STAT_RANGES.map(r => r[0]);
const presetOr = (range, fb) => STAT_RANGE_KEYS.indexOf(range) >= 0 ? range : fb;
const SORTS = [["views", "Views"], ["uniqueUsers", "Unique Users"], ["engagements", "Engagements"], ["conversions", "Conversions"], ["salesQuantity", "Units Sold"], ["forecastSalesQuantity", "Forecast (12 mo)"]];
const PAGE = 50;

// Paired precomputed range for Δ chips (FE-only join). Ranges without a
// precomputed prior (last_*, last_7_days, all_time) show no chips.
const PAIRS = {
  today: "yesterday",
  this_week: "last_week",
  this_month: "last_month",
  this_quarter: "last_quarter",
  this_year: "last_year"
};

// Elapsed fraction of the CURRENT period — the prior full period is scaled
// by this before comparing (pace clamp), so a half-elapsed month doesn't
// read as "down 50%".
function elapsedFraction(range) {
  const now = new Date();
  let f = 1;
  if (range === "today") f = (now.getHours() + 1) / 24;else if (range === "this_week") f = ((now.getDay() + 6) % 7 + 1) / 7;else if (range === "this_month") {
    const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    f = now.getDate() / days;
  } else if (range === "this_quarter") {
    const q = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), q * 3, 1);
    const end = new Date(now.getFullYear(), q * 3 + 3, 1);
    f = (now - start) / (end - start);
  } else if (range === "this_year") {
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear() + 1, 0, 1);
    f = (now - start) / (end - start);
  }
  return Math.min(1, Math.max(0.05, f));
}
const HELP = {
  page: "How every product is doing — web interest, sales, and a 12-month forecast, side by side. Updated nightly from your tracking and sales data.",
  views: "Product page views in the selected range. More views = more shoppers considering it.",
  users: "Unique visitors who viewed this product. A high views-per-user ratio means shoppers keep coming back to it.",
  engagements: "Actions taken on the product — tearsheets, finish clicks, wishlist adds. Engagement predicts orders better than views alone.",
  units: "Units from your sales feed in this range, and how many distinct customers bought. 8 units / 7 customers reads very differently than 8 / 1.",
  forecast: "Units we expect over the next 12 months, from this product's recent sales pace and seasonality. Refreshed nightly. Directional — use it to rank products, not to promise inventory.",
  interested: "Contacts who viewed or engaged with this product and are in your contact database — the warm list. Click through to see and message them.",
  interest: "Distinct viewers per day across the selected range.",
  delta: "vs the prior equal period, pace-adjusted for partial periods."
};
function fetchSalesApi(api) {
  const Service = ServiceFactory.getType(api, "product");
  const service = new Service();
  return service.getAsync();
}

// model field → whitelisted snake_case DB column for $orderBy
const ORDER_COLS = {
  views: "views",
  uniqueUsers: "unique_users",
  engagements: "engagements",
  conversions: "conversions",
  salesQuantity: "sales_quantity",
  forecastSalesQuantity: "forecast_sales_quantity"
};
function fetchStats(range, {
  sort = "views",
  filter = "",
  skip = 0,
  top = PAGE,
  category = "",
  accountKv = ""
} = {}) {
  const col = ORDER_COLS[sort] || "views";
  let api = `/product/performance/stats/${range}?$skip=${skip}&$top=${top}` + `&$orderBy=${encodeURIComponent(`${col} desc`)}`;
  const q = filter.trim();
  if (q) api += `&q=${encodeURIComponent(q)}`;
  if (category) api += `&category=${encodeURIComponent(category)}`;
  // Account lens (architecture B): triggers the server's on-read path (sales-only cols).
  if (accountKv) api += `&${accountKv}`;
  const Service = ServiceFactory.getType(api, "product");
  const service = new Service();
  return service.getAsync();
}
class ProductPerformanceViewComponent extends React.Component {
  constructor(props) {
    super(props);
    // Prefill the filter from a ?q= param (global-search "See all products").
    _defineProperty(this, "loadMore", async () => {
      const seq = this._loadSeq;
      const {
        rows,
        sort,
        filter
      } = this.state;
      this.setState({
        loadingMore: true
      });
      try {
        const res = await fetchStats(this.range(), {
          sort,
          filter,
          skip: rows.length,
          category: this.props.category,
          accountKv: this.props.accountKv
        });
        if (!this._alive || seq !== this._loadSeq) return;
        const items = res.items || [];
        const all = rows.concat(items);
        this.setState({
          rows: all,
          more: items.length === PAGE && all.length < this.state.count,
          loadingMore: false
        });
      } catch (e) {
        if (this._alive) this.setState({
          loadingMore: false
        });
      }
    });
    _defineProperty(this, "onRange", v => {
      this.props.history.push(`/ProductPerformance/${v}`);
    });
    _defineProperty(this, "onSort", v => {
      this.setState({
        sort: v,
        rows: null
      }, () => this.refetch());
    });
    _defineProperty(this, "onFilter", e => {
      const filter = e.target.value;
      this.setState({
        filter
      });
      clearTimeout(this._filterTimer);
      this._filterTimer = setTimeout(() => {
        if (this._alive) this.refetch();
      }, 400);
    });
    _defineProperty(this, "openProduct", p => {
      // Product detail is available to retail too (not just manufacturer):
      // the page shows the universal sections (KPIs, trend, forecast,
      // customers, contacts, configs); the rep/territory sections self-hide.
      this.props.history.push({
        pathname: `/Products/${this.range()}/product/${encodeURIComponent(p.productId)}`,
        state: p.title ? {
          title: p.title
        } : undefined
      });
    });
    _defineProperty(this, "openForecasts", () => {
      this.props.history.push("/Forecast/Product");
    });
    // "Forecast a look-alike" — seed a Similarity Forecast from this
    // product's image via the existing multipart contract (similarity.jsx).
    _defineProperty(this, "lookAlike", async (e, p) => {
      e.stopPropagation();
      if (this.state.seeding || !p.imageLink) return;
      this.setState({
        seeding: p.productId
      });
      const toast = document.querySelector("paper-toast");
      try {
        const img = await fetch(p.imageLink);
        if (!img.ok) throw new Error("image fetch " + img.status);
        const blob = await img.blob();
        const fd = new FormData();
        const ext = (p.imageLink.split("?")[0].split(".").pop() || "jpg").slice(0, 5);
        fd.append("file", blob, `${p.productId}.${ext}`);
        // header values must be ASCII-safe
        const ascii = s => String(s || "").replace(/[^\x20-\x7E]/g, "").slice(0, 120);
        // X-CATEGORY is required (non-empty) and drives comparable
        // matching. Stats rows carry no product type, so use the
        // title's trailing type word ("Hayden Swivel Chair" → Chair).
        const baseTitle = String(p.title || "").split("-")[0].replace(/\(.*?\)/g, "").trim();
        const lastWord = baseTitle.split(/\s+/).pop() || "";
        const category = /^[A-Za-z]{3,}$/.test(lastWord) ? lastWord : "Furniture";
        const res = await fetch(endpoints.forecast.root + "/forecast/product-similarity-forecast", {
          method: "POST",
          headers: {
            Authorization: "Bearer " + (AuthBase.idToken || ""),
            "X-APP-ID": (account.get() || {}).tenantId || "",
            "X-NAME": ascii(`${p.title || p.productId} - look-alike`),
            "X-DESCRIPTION": ascii(`Seeded from Product Performance (${p.productId})`),
            "X-CATEGORY": ascii(category)
          },
          body: fd
        });
        if (!res.ok) throw new Error("HTTP " + res.status);
        if (toast) toast.show("Similarity forecast queued — results appear in Product Forecasts in a few minutes.");
        this.props.history.push("/Forecast/Similarity");
      } catch (err) {
        if (toast) toast.show("Could not seed the look-alike forecast. Try again, or upload the image from Product Forecasts.");
        if (this._alive) this.setState({
          seeding: null
        });
      }
    });
    let initialFilter = "";
    try {
      const qp = new URLSearchParams(window.location.search).get("q");
      if (qp) initialFilter = qp;
    } catch (e) {/* older browser / no search */}
    this.state = {
      rows: null,
      count: 0,
      more: false,
      loadingMore: false,
      error: false,
      sort: "views",
      filter: initialFilter,
      sellers: [],
      prior: null,
      // productId → {views, conversions} of the paired range
      seeding: null // productId currently being seeded as a look-alike
    };
    this._alive = true;
    this._filterTimer = null;
    this._loadSeq = 0;
  }
  range() {
    return this.props.params && this.props.params.range || presetOr(getGlobalRange("this_month"), "this_month");
  }
  componentDidMount() {
    this.load();
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range) return;
      // Embedded in the Product Explorer: reload in place instead of routing
      // to the standalone /ProductPerformance page.
      if (this.props.embedded) {
        this.setState({
          rows: null,
          error: false,
          prior: null
        }, () => this.load());
        return;
      }
      const r = presetOr(range, "this_month");
      const target = `/ProductPerformance/${r}`;
      if (window.location.pathname === target) {
        this.setState({
          rows: null,
          error: false,
          prior: null
        }, () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._filterTimer);
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const rangeChanged = (prev.params && prev.params.range || "") !== (this.props.params && this.props.params.range || "");
    const catChanged = (prev.category || "") !== (this.props.category || "");
    if (rangeChanged || catChanged) {
      this.setState({
        rows: null,
        error: false,
        prior: null
      });
      this.load();
    }
  }
  async load() {
    const seq = ++this._loadSeq;
    const range = this.range();
    const {
      sort,
      filter
    } = this.state;
    try {
      const wantSales = hasTenantFeature("salesTransactions");
      const pairedRange = PAIRS[range];
      const [res, sellers, paired] = await Promise.all([fetchStats(range, {
        sort,
        filter,
        category: this.props.category,
        accountKv: this.props.accountKv
      }), wantSales ? fetchSalesApi(`/sales/products/${range}?top=15${this.props.category ? `&category=${encodeURIComponent(this.props.category)}` : ""}`).catch(() => []) : Promise.resolve([]), pairedRange ? fetchStats(pairedRange, {
        sort: "views",
        top: 1000
      }).catch(() => null) : Promise.resolve(null)]);
      if (!this._alive || seq !== this._loadSeq) return;
      let prior = null;
      if (paired && paired.items) {
        prior = {};
        paired.items.forEach(p => {
          prior[p.productId] = {
            views: Number(p.views) || 0,
            conversions: Number(p.conversions) || 0
          };
        });
      }
      const items = res.items || [];
      this.setState({
        rows: items,
        count: Number(res.count) || items.length,
        more: items.length === PAGE && items.length < (Number(res.count) || 0),
        sellers,
        prior
      });
    } catch (e) {
      if (this._alive && seq === this._loadSeq) this.setState({
        error: true
      });
    }
  }

  // Refetch page 1 (sort/filter changed) without dropping sellers/prior.
  async refetch() {
    const seq = ++this._loadSeq;
    const {
      sort,
      filter
    } = this.state;
    try {
      const res = await fetchStats(this.range(), {
        sort,
        filter,
        category: this.props.category,
        accountKv: this.props.accountKv
      });
      if (!this._alive || seq !== this._loadSeq) return;
      const items = res.items || [];
      this.setState({
        rows: items,
        count: Number(res.count) || items.length,
        more: items.length === PAGE && items.length < (Number(res.count) || 0)
      });
    } catch (e) {
      if (this._alive && seq === this._loadSeq) this.setState({
        error: true
      });
    }
  }
  deltaChip(p, field) {
    const {
      prior
    } = this.state;
    if (!prior) return null;
    const before = prior[p.productId];
    if (!before) return null;
    const raw = Number(before[field]) || 0;
    // noise floor: tiny priors make silly chips (+1441% off a scaled
    // prior of 1-2) — require a meaningful base before showing a delta
    if (raw < 10) return null;
    const scaled = raw * elapsedFraction(this.range());
    if (scaled < 1) return null;
    const cur = Number(p[field]) || 0;
    const pct = Math.round((cur - scaled) / scaled * 100);
    if (!pct) return null;
    return /*#__PURE__*/React.createElement("span", {
      className: "delta " + (pct > 0 ? "up" : "dn"),
      title: HELP.delta
    }, pct > 0 ? `+${pct}%` : `−${Math.abs(pct)}%`);
  }
  render() {
    const {
      rows,
      count,
      more,
      loadingMore,
      error,
      sort,
      filter,
      sellers,
      seeding
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!rows) return /*#__PURE__*/React.createElement(Loader, null);
    const range = this.range();
    const drill = true; // product detail is available to all business models
    const statsById = {};
    rows.forEach(p => {
      statsById[p.productId] = p;
    });
    const q = filter.trim().toLowerCase();
    // `count` is filter-aware on the where= path, so it's always honest.
    const countLabel = num(count);
    const cats = this.props.embedded ? this.props.categories || [] : [];
    const selCat = this.props.category || "";
    const searchInput = /*#__PURE__*/React.createElement("input", {
      className: "cust-search wa-search",
      type: "search",
      style: {
        minWidth: "220px"
      },
      placeholder: "Filter products",
      value: filter,
      onChange: this.onFilter
    });
    const sortDrop = /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "180px",
      value: sort,
      onChange: this.onSort,
      options: SORTS.map(([v, l]) => [v, `Sort: ${l}`])
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view product-performance wa-view"
    }, this.props.embedded ?
    /*#__PURE__*/
    // Embedded in the Explorer: one unified toolbar row — search · category chips · sort.
    React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginBottom: 14
      }
    }, cats.length > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, /*#__PURE__*/React.createElement("button", {
      className: selCat === "" ? "on" : "",
      onClick: () => this.props.onCategory && this.props.onCategory("")
    }, "All"), cats.slice(0, 8).map(c => /*#__PURE__*/React.createElement("button", {
      key: c.category,
      className: selCat === c.category ? "on" : "",
      title: catDecode(c.category),
      onClick: () => this.props.onCategory && this.props.onCategory(c.category)
    }, catLabel(c.category)))) : null, /*#__PURE__*/React.createElement(Filters, null, searchInput, sortDrop)) : /*#__PURE__*/React.createElement(PageHeader, {
      title: "Product performance",
      subtitle: "Web interest, sales and a 12-month forecast side by side \xB7 updated nightly",
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, searchInput, sortDrop)
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(BestFitPanel, {
      range: range,
      statsById: statsById,
      category: this.props.category
    }), sellers && sellers.length > 0 && /*#__PURE__*/React.createElement(Card, {
      title: "Top sellers",
      hint: "by revenue in this range",
      span: 12
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, sellers.filter(p => !q || (p.title || "").toLowerCase().includes(q) || (p.productId || "").toLowerCase().includes(q)).map(p => {
      const st = statsById[p.productId];
      return /*#__PURE__*/React.createElement("tr", {
        key: p.productId,
        className: drill ? "clickable" : undefined,
        onClick: () => this.openProduct({
          productId: p.productId,
          title: p.title
        }),
        title: drill ? `Open ${p.title || p.productId} product detail` : undefined
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          alignItems: "center",
          gap: "10px"
        }
      }, st && st.imageLink ? /*#__PURE__*/React.createElement("img", {
        src: st.imageLink,
        alt: "",
        loading: "lazy",
        style: {
          width: "36px",
          height: "36px",
          objectFit: "contain",
          flex: "0 0 auto"
        }
      }) : null, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, cleanTitle(p.title) || p.productId), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, p.sku || (/^shopify:/i.test(String(p.productId || "")) ? "" : p.productId))))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.orders)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.quantity)), /*#__PURE__*/React.createElement("td", {
        className: "r strong"
      }, usd(p.totalAmount)));
    }))))), /*#__PURE__*/React.createElement(Card, {
      title: "Products",
      hint: `web engagement and sales · ${countLabel} · sorted by ${(SORTS.find(([v]) => v === sort) || [null, "Views"])[1].toLowerCase()} · click a product for its sales trend and forecast`,
      span: 12
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Price"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: HELP.views
    }, "Views"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: HELP.users
    }, "Users"), /*#__PURE__*/React.createElement("th", {
      title: HELP.interest
    }, "Interest"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: HELP.engagements
    }, "Engagements"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conv"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: HELP.units
    }, "Units Sold"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: HELP.units
    }, "Customers"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: HELP.forecast
    }, "Forecast \xB7 12 mo"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: HELP.interested
    }, "Interested"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }))), /*#__PURE__*/React.createElement("tbody", null, rows.map(p => /*#__PURE__*/React.createElement("tr", {
      key: p.productId,
      className: drill ? "clickable" : undefined,
      onClick: () => this.openProduct(p),
      title: drill ? `Open ${p.title || p.productId} product detail` : undefined
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: "10px"
      }
    }, p.imageLink ? /*#__PURE__*/React.createElement("img", {
      src: p.imageLink,
      alt: "",
      loading: "lazy",
      style: {
        width: "36px",
        height: "36px",
        objectFit: "contain",
        flex: "0 0 auto"
      },
      onError: e => {
        e.target.style.display = "none";
      }
    }) : /*#__PURE__*/React.createElement("span", {
      style: {
        width: "36px",
        height: "36px",
        flex: "0 0 auto",
        borderRadius: "6px",
        background: "#f2f2f2",
        display: "grid",
        placeItems: "center"
      },
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons",
      style: {
        fontSize: "20px",
        color: "#c4c4c4"
      }
    }, "weekend")), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, cleanTitle(p.title) || p.productId), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, p.sku || (/^shopify:/i.test(String(p.productId || "")) ? "" : p.productId))))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, p.price ? usd(p.price) : "—"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.views), " ", this.deltaChip(p, "views")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.uniqueUsers)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(InterestSpark, {
      productId: p.productId,
      range: range
    })), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.engagements)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.conversions), " ", this.deltaChip(p, "conversions")), /*#__PURE__*/React.createElement("td", {
      className: "r strong"
    }, num(p.salesQuantity)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.customers)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, p.lastForecastDate ? Number(p.forecastSalesQuantity) < 0 ? "—" : num(Math.round(Number(p.forecastSalesQuantity) || 0)) : /*#__PURE__*/React.createElement("span", {
      title: "Needs 2+ months of history to forecast."
    }, "\u2014")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(p.contactsInterested)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, p.imageLink ? /*#__PURE__*/React.createElement("button", {
      className: "pp-action",
      title: "Forecast a look-alike \u2014 seeds a Similarity Forecast from this product's image",
      disabled: !!seeding,
      onClick: e => this.lookAlike(e, p)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, seeding === p.productId ? "hourglass_top" : "auto_awesome")) : null)))))), rows.length === 0 && /*#__PURE__*/React.createElement(Empty, null, range === "last_7_days" ? "Last 7 days builds from the next nightly snapshot. Check back tomorrow." : q ? "No products match this filter." : "Your products will appear here after the first nightly snapshot, usually within 24 hours of connecting."), rows.length === 0 && !q && range !== "last_7_days" && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 12
      }
    }, /*#__PURE__*/React.createElement(SetupHint, {
      what: "products"
    })), more && /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: this.loadMore,
      disabled: loadingMore
    }, loadingMore ? "Loading…" : "Load more")), /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Looking for the batch similarity forecasts? ", /*#__PURE__*/React.createElement("a", {
      onClick: this.openForecasts,
      style: {
        cursor: "pointer"
      }
    }, "Product forecasts \u2192")))));
  }
}
export default withRouter(ProductPerformanceViewComponent);